import { PLATFORM_THEMES } from "@playbookai/types";
import type { PresentationTheme } from "./types.js";

/**
 * The theme the sign-in page and the app share (ADR-0092, 2026-10-06
 * amendment). `pb_theme` is a COPY of the theme on screen, not a setting:
 * Playbook BE owns the setting (ADR-0074), and the cookie only lets the
 * Keycloak sign-in page, and the app's first moment, agree with it. Keycloak
 * lives on the app's own host under `/keycloak`, so `Path=/` reaches both;
 * locally the ports differ, and cookies ignore the port. It is not HttpOnly
 * because the sign-in page's own script reads it, and it holds no secret.
 * Safari drops script-written cookies after 7 days without a visit; the
 * fallback is the device, which is Match device.
 *
 * `pb_theme_chosen` is the sign-in page's mark for a CLICK on its switch:
 * `<theme>.<click time in ms>`, 30 minutes. It carries the choice itself
 * because loading the saved setting rewrites `pb_theme` first (d5).
 */
export const THEME_COOKIE = "pb_theme";
export const THEME_CHOSEN_COOKIE = "pb_theme_chosen";
const ONE_YEAR_SECONDS = 31_536_000;

/** A Set-Cookie string for `document.cookie`, with the attributes both sides use. */
export function themeCookieString(
  name: string,
  value: string,
  maxAgeSeconds: number,
  secure: boolean,
): string {
  return (
    `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax` +
    (secure ? "; Secure" : "")
  );
}

function isTheme(value: string | null): value is PresentationTheme {
  return (PLATFORM_THEMES as readonly string[]).includes(value ?? "");
}

/* A cookie's raw value, or null when it is not set. */
function rawCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie ? document.cookie.split("; ") : []) {
    const eq = part.indexOf("=");
    if (part.slice(0, eq) === name) return part.slice(eq + 1);
  }
  return null;
}

/* A value that is not valid percent-encoding reads as null, the same as an
   unknown one: decodeURIComponent throws on it, and a display preference must
   never stop the app from starting (PR 396 review D1). */
function decoded(raw: string | null): string | null {
  if (raw === null) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  document.cookie = themeCookieString(
    name,
    value,
    maxAgeSeconds,
    window.location.protocol === "https:",
  );
}

/** The shared theme, or null when there is none or it is not one of the four. */
export function readThemeCookie(): PresentationTheme | null {
  const value = decoded(rawCookie(THEME_COOKIE));
  return isTheme(value) ? value : null;
}

/** Mirrors the theme now on screen for the sign-in page. */
export function writeThemeCookie(theme: PresentationTheme): void {
  writeCookie(THEME_COOKIE, theme, ONE_YEAR_SECONDS);
}

export interface SignInChoice {
  readonly theme: PresentationTheme;
  /** When the person clicked, in ms since the epoch (the browser's clock). */
  readonly chosenAt: number;
}

/**
 * Reads the sign-in page's mark and deletes it in the same step, so a second
 * read finds nothing. A malformed mark, including one that is not valid
 * percent-encoding, counts as no mark, and is deleted too.
 */
export function takeSignInChoice(): SignInChoice | null {
  const raw = rawCookie(THEME_CHOSEN_COOKIE);
  if (raw === null) return null;
  writeCookie(THEME_CHOSEN_COOKIE, "", 0);
  const value = decoded(raw);
  const match = value === null ? null : /^([a-z-]+)\.(\d{1,16})$/.exec(value);
  const theme = match?.[1] ?? null;
  if (!match || !isTheme(theme)) return null;
  return { theme, chosenAt: Number(match[2]) };
}
